// KPI listelerinin (Özet, Disclosure, Hedefler, portal Özet) kategori ağacı görünümü — saf mantık.
// Satır türü TEK kaynak: shared `effectiveNodeKind` (kpi-grid-breakdown `rowKindOf` üzerinden);
// bant = kategori / yapı satırı / kırılım grubu, diğerleri veri satırı. Sıra: set göstergelerinin
// dizideki sırası (kardeşler arası), derinlik = setteki ata sayısı. Bileşen yok.

import type { KpiResolvedIndicator } from "@mahir/shared";
import { rowKindOf } from "./kpi-grid-breakdown";

export interface TreeListItem<T> {
  /** set göstergesi kimliği; sette yoksa null (ağaç dışı satır — sona, derinlik 0) */
  indicatorId: string | null;
  /** etkin sekme süzgecine uyuyor mu */
  matches: boolean;
  data: T;
}

export type TreeListRow<T> =
  | {
      kind: "band";
      indicator: KpiResolvedIndicator;
      depth: number;
      collapsed: boolean;
      /** banttaki (alt bantlar dahil) toplam veri satırı */
      total: number;
      /** bunlardan süzgece uyanlar */
      matching: number;
    }
  | { kind: "item"; data: T; indicatorId: string | null; depth: number };

export interface BuildOptions {
  collapsed: ReadonlySet<string>;
  /** "Tümü" dışı sekme: uymayan satır ve boş kalan bant gizlenir */
  filtered: boolean;
}

export function buildTreeList<T>(
  indicators: readonly KpiResolvedIndicator[],
  items: readonly TreeListItem<T>[],
  opts: BuildOptions,
): TreeListRow<T>[] {
  const ids = new Set(indicators.map((i) => i.id));
  const kids = new Map<string, KpiResolvedIndicator[]>();
  const roots: KpiResolvedIndicator[] = [];
  for (const i of indicators) {
    if (i.parentId !== null && ids.has(i.parentId)) {
      const list = kids.get(i.parentId) ?? [];
      list.push(i);
      kids.set(i.parentId, list);
    } else roots.push(i);
  }
  // kardeşler orderIndex sırasıyla (eşitlikte dizideki sıra — Array.sort kararlı)
  const bySibling = (a: KpiResolvedIndicator, b: KpiResolvedIndicator): number =>
    a.orderIndex - b.orderIndex;
  roots.sort(bySibling);
  for (const list of kids.values()) list.sort(bySibling);
  const byInd = new Map<string, TreeListItem<T>[]>();
  const orphans: TreeListItem<T>[] = [];
  for (const it of items) {
    if (it.indicatorId !== null && ids.has(it.indicatorId)) {
      // aynı gösterge birden çok satırda olabilir (Disclosure: bir gösterge, birden çok standart kodu)
      byInd.set(it.indicatorId, [...(byInd.get(it.indicatorId) ?? []), it]);
    } else orphans.push(it);
  }

  interface Visit {
    rows: TreeListRow<T>[];
    total: number;
    matching: number;
  }
  const visit = (node: KpiResolvedIndicator, depth: number): Visit => {
    const children = kids.get(node.id) ?? [];
    const kind = rowKindOf(node, children);
    const acc: Visit = { rows: [], total: 0, matching: 0 };
    const isBand = kind === "band" || kind === "group";
    // bant düğümüne eşlenmiş satır (Disclosure: kategoriye eşleme) bantın ALTINDA, depth+1'de görünür
    const own = byInd.get(node.id) ?? [];
    const ownDepth = isBand ? depth + 1 : depth;
    for (const o of own) {
      acc.total += 1;
      if (o.matches) acc.matching += 1;
      if (!opts.filtered || o.matches) {
        acc.rows.push({ kind: "item", data: o.data, indicatorId: node.id, depth: ownDepth });
      }
    }
    for (const c of children) {
      const v = visit(c, depth + 1);
      acc.rows.push(...v.rows);
      acc.total += v.total;
      acc.matching += v.matching;
    }
    if (!isBand) return acc;
    if (opts.filtered && acc.matching === 0) return { rows: [], total: acc.total, matching: 0 };
    const collapsed = opts.collapsed.has(node.id);
    return {
      rows: [
        {
          kind: "band",
          indicator: node,
          depth,
          collapsed,
          total: acc.total,
          matching: acc.matching,
        },
        ...(collapsed ? [] : acc.rows),
      ],
      total: acc.total,
      matching: acc.matching,
    };
  };

  const out: TreeListRow<T>[] = [];
  for (const r of roots) out.push(...visit(r, 0).rows);
  for (const o of orphans) {
    if (!opts.filtered || o.matches) {
      out.push({ kind: "item", data: o.data, indicatorId: null, depth: 0 });
    }
  }
  return out;
}

function walksTo(
  parentOf: ReadonlyMap<string, string | null>,
  ind: { parentId: string | null },
  ancestorId: string,
): boolean {
  const seen = new Set<string>();
  let cur = ind.parentId;
  while (cur && !seen.has(cur)) {
    if (cur === ancestorId) return true;
    seen.add(cur);
    cur = parentOf.get(cur) ?? null;
  }
  return false;
}

interface TreeNode {
  id: string;
  parentId: string | null;
}

const parentMap = (indicators: readonly TreeNode[]): Map<string, string | null> =>
  new Map(indicators.map((i) => [i.id, i.parentId]));

/**
 * `ind`'in (kendisi hariç) atalarından biri `ancestorId` mi — üst zinciri yürüyerek. Dizi sırasına
 * bağlı değildir (grid.indicators kardeş-yerel `orderIndex` ile sıralı; çocuk üstünden önce gelebilir).
 */
export function isUnderAncestor(
  indicators: readonly TreeNode[],
  ind: { parentId: string | null },
  ancestorId: string,
): boolean {
  return walksTo(parentMap(indicators), ind, ancestorId);
}

/** Kategori süzgeci: `rootId` ve tüm torunları (dizi sırası korunur); `rootId` null → hepsi. */
export function subtreeIndicators<T extends TreeNode>(
  indicators: readonly T[],
  rootId: string | null,
): T[] {
  if (!rootId) return [...indicators];
  const parentOf = parentMap(indicators);
  return indicators.filter((i) => i.id === rootId || walksTo(parentOf, i, rootId));
}

/** Ağaçta bant olan tüm düğümler (Tümünü daralt). */
export function allBandIds(indicators: readonly KpiResolvedIndicator[]): string[] {
  const kids = new Map<string, KpiResolvedIndicator[]>();
  for (const i of indicators) {
    if (i.parentId !== null) kids.set(i.parentId, [...(kids.get(i.parentId) ?? []), i]);
  }
  return indicators
    .filter((i) => {
      const k = rowKindOf(i, kids.get(i.id) ?? []);
      return k === "band" || k === "group";
    })
    .map((i) => i.id);
}

export function toggleBand(collapsed: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(collapsed);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Ağaç girintisi: 22 px/seviye, ≤1024 px'te 14 px (CheckTree ile aynı). */
export function treeIndent(depth: number, narrow: boolean): number {
  return depth * (narrow ? 14 : 22);
}

const storageKey = (userId: string, screen: string): string =>
  `kpi.treeCollapsed.${userId}.${screen}`;

export function loadCollapsed(userId: string, screen: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(storageKey(userId, screen));
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

export function saveCollapsed(userId: string, screen: string, set: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(storageKey(userId, screen), JSON.stringify([...set]));
  } catch {
    // depolama kapalı — kalıcılık isteğe bağlı
  }
}

/**
 * "Tümünü aç/kapat" (Paketler ağacı): bantlardan biri kapalıysa hepsi açılır (boş küme), hepsi açıksa
 * hepsi kapanır. Kimlikler çağıranın o an GÖRÜNEN bantlarıdır; kapalı kümedeki bilinmeyen kimlikler
 * (süzgeç dışı bantlar) korunur.
 */
export function toggleAllCollapsed(
  bandIds: readonly string[],
  collapsed: ReadonlySet<string>,
): Set<string> {
  const anyClosed = bandIds.some((id) => collapsed.has(id));
  const next = new Set(collapsed);
  for (const id of bandIds) {
    if (anyClosed) next.delete(id);
    else next.add(id);
  }
  return next;
}
